/**
 * JitsiMeetingRoom — embedded, passwordless video/audio conference.
 *
 * Uses Jitsi Meet's External API (public meet.jit.si host). The room
 * name is a 32-char hex minted server-side and handed only to
 * verified committee members via /api/meeting-rooms/{id}/join — the
 * room name IS the capability, so no passwords are needed.
 */
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

const EXT_API_SRC = "https://meet.jit.si/external_api.js";

function loadJitsiScript() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve(window.JitsiMeetExternalAPI);
    let s = document.querySelector(`script[src="${EXT_API_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = EXT_API_SRC;
      s.async = true;
      document.body.appendChild(s);
    }
    s.addEventListener("load",  () => resolve(window.JitsiMeetExternalAPI));
    s.addEventListener("error", reject);
  });
}

export default function JitsiMeetingRoom({ join, onClose }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);

  useEffect(() => {
    if (!join?.jitsi_room) return;
    let cancelled = false;

    loadJitsiScript().then((JitsiMeetExternalAPI) => {
      if (cancelled || !containerRef.current) return;
      apiRef.current = new JitsiMeetExternalAPI(join.jitsi_domain || "meet.jit.si", {
        roomName: join.jitsi_room,
        parentNode: containerRef.current,
        width: "100%",
        height: "100%",
        userInfo: { displayName: join.display_name || "Committee" },
        configOverwrite: {
          prejoinPageEnabled:   false,   // straight in — passwordless
          startWithAudioMuted:  false,
          startWithVideoMuted:  false,
          disableDeepLinking:   true,
          enableClosePage:      false,
        },
        interfaceConfigOverwrite: {
          MOBILE_APP_PROMO: false,
          SHOW_JITSI_WATERMARK: false,
          SHOW_WATERMARK_FOR_GUESTS: false,
          DEFAULT_BACKGROUND: "#0a0a0a",
        },
      });
      apiRef.current.addEventListener("readyToClose", () => onClose?.());
    });

    return () => {
      cancelled = true;
      try { apiRef.current?.dispose(); } catch { /* noop */ }
      apiRef.current = null;
    };
  }, [join?.jitsi_room, join?.jitsi_domain, join?.display_name, onClose]);

  if (!join?.jitsi_room) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex flex-col"
         data-testid="jitsi-meeting-modal">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-300">
            Live · Committee Room
          </div>
          <div className="font-display text-lg mt-0.5" data-testid="jitsi-meeting-title">
            {join.title || "Meeting"}
          </div>
        </div>
        <Button variant="ghost"
                className="h-9 gap-2 text-white/75 hover:text-white"
                onClick={onClose}
                data-testid="jitsi-close-btn">
          <X size={16}/> Leave
        </Button>
      </div>
      <div ref={containerRef} className="flex-1 min-h-0 bg-black"
           data-testid="jitsi-container" />
    </div>
  );
}
